import type { CompanyTechnologyNestedSelectDto } from '@@types/api.types';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { Tile } from '@components/shared/tile.component';
import { buildCompanyLocationDisplay, formatCompanyType } from '@utils/company-display.util';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { ArrowRightIcon, Building2, MapPin } from 'lucide-react';
import { FC, useMemo } from 'react';

type TechnologyOrganizationTileProps = {
  company?: CompanyTechnologyNestedSelectDto;
  isLoading?: boolean;
  onNavigateToTab?: (tab: TechnologyHeaderTab) => void;
};

export const TechnologyOrganizationTile: FC<TechnologyOrganizationTileProps> = (props) => {
  const { company, isLoading, onNavigateToTab } = props;

  const locationDisplay = useMemo(() => (company ? buildCompanyLocationDisplay(company) : undefined), [company]);
  const companyTypeLabel = useMemo(() => formatCompanyType(company?.type), [company?.type]);

  const canNavigate = Boolean(onNavigateToTab);

  return (
    <Tile
      emptyState={{ title: 'No Organization Data' }}
      header={{
        iconButton: canNavigate
          ? {
              icon: <ArrowRightIcon size={16} />,
              onClick: () => onNavigateToTab?.('organization'),
            }
          : undefined,
        title: 'Organization',
      }}
      hoverReveal={canNavigate}
      isEmpty={!company}
      isLoading={isLoading}
    >
      {company && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <CompanyLogoPreview
              className="h-10 w-10 shrink-0 rounded-lg"
              fallback={<Building2 className="size-4 text-gray-400" />}
              logoAlt={`${company.name} logo`}
              logoUrl={company.logoUrl}
            />
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-medium text-gray-900">{company.name}</span>
              {companyTypeLabel && <span className="truncate text-xs text-gray-500">{companyTypeLabel}</span>}
            </div>
          </div>

          {locationDisplay && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
              <span className="truncate">{locationDisplay}</span>
            </div>
          )}

          {company.description && <p className="line-clamp-3 text-sm text-gray-600">{company.description}</p>}
        </div>
      )}
    </Tile>
  );
};
